import React, { useState } from "react";
import {
    TableCell,
    TableRow,
    Typography,
    Stack,
    Switch,
    Button,
    Chip,
    Box,
} from "@mui/material";
import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useUpdateRegionsMutation } from "../../../store/masters/mastersApis";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";

const RegionsTableComponent = ({ loading, fetching = false, count, data, edit, pagination, handlePagination }) => {
    const [handleUpdate] = useUpdateRegionsMutation();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [regionIdToDelete, setRegionIdToDelete] = useState(null);

    const handleDisable = (row) => {
        setRegionIdToDelete(row);
        setConfirmationOpen(true);
    };

    const handleConfirmDisable = () => {
        handleUpdate({
            id: regionIdToDelete?.id,
            name: regionIdToDelete?.name,
            code: regionIdToDelete?.code,
            is_active: !regionIdToDelete?.is_active
        });
        setConfirmationOpen(false);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
    };

    let columns = [
        {
            title: "ID",
            field: "id",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "Region Name",
            field: "name",
            sort: true,
            width: "35%",
            align: "left",
        },
        {
            title: "Region Code",
            field: "code",
            sort: true,
            width: "20%",
            align: "center",
        },
        {
            title: "Status",
            field: "is_active",
            sort: false,
            width: "15%",
            align: "center",
        },
        {
            title: "Created Date",
            field: "created_at",
            sort: true,
            width: "15%",
            align: "center",
        },
        {
            title: "",
            name: "",
            sort: false,
            width: "10%",
            align: "center",
        },
    ];

    const renderTableData = !loading && data && data.length > 0 ? (
        data.map(function (row, index) {
            return (
                <TableRow
                    key={index}
                    sx={{ 
                        "td, th": { 
                            border: 0, 
                            padding: "16px 12px",
                            verticalAlign: "middle",
                        }, 
                        ...(row?.is_active === false && { 
                            opacity: 0.6,
                            backgroundColor: '#fafafa',
                        }),
                        '&:hover': {
                            backgroundColor: '#f8f9fa',
                        },
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <TableCell align="center">
                        <Chip
                            label={row.id}
                            size="small"
                            sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                            }}
                        />
                    </TableCell>
                    <TableCell align="left">
                        <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ 
                                fontWeight: 600,
                                color: '#2c3e50',
                                fontSize: '0.875rem',
                            }}>
                                {row?.name}
                            </Typography>
                            {row?.is_active === false && (
                                <Chip
                                    label="Inactive"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#ffebee',
                                        color: '#d32f2f',
                                        fontSize: '0.7rem',
                                        height: 20,
                                        width: 'fit-content',
                                    }}
                                />
                            )}
                        </Stack>
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            label={row?.code}
                            size="small"
                            sx={{
                                backgroundColor: '#fff3e0',
                                color: '#f57c00',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                height: 28,
                                border: '1px solid #ffcc02',
                            }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            label={row?.is_active ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                                backgroundColor: row?.is_active ? '#e8f5e8' : '#ffebee',
                                color: row?.is_active ? '#2e7d32' : '#d32f2f',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                            }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="caption" sx={{ 
                            color: "text.secondary",
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}>
                            {handleDateTimeDefault(row?.created_at)}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <ActionMenu
                            actions={[
                                {
                                    ...ActionTypes.EDIT,
                                    onClick: () => edit(row, "Edit")
                                }
                            ]}
                            row={row}
                            onStatusToggle={handleDisable}
                        />
                    </TableCell>
                </TableRow>
            );
        })
    ) : (
        <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
            <TableCell colSpan={6} align="center">
                <Typography variant="body2" sx={{ color: '#666', py: 4 }}>
                    {loading ? 'Loading regions...' : 'No regions found'}
                </Typography>
            </TableCell>
        </TableRow>
    );

    return (
        <>
            <TableCommon 
                columns={columns} 
                tableData={renderTableData} 
                count={count} 
                loading={loading || fetching} 
                pagination={pagination} 
                handlePagination={handlePagination}
                emptyMessage="No regions found"
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmationOpen}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmDisable}
                title={regionIdToDelete?.is_active === false ? "Are you sure you want to activate selected region?" : "Are you sure you want to deactivate selected region?"}
                confirmButtonTitle={regionIdToDelete?.is_active === false ? "Yes, Activate" : "Yes, Deactivate"}
            />
        </>
    );
};

export default RegionsTableComponent;